import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormField,
  Modal,
  RadioGroup,
  Select,
  SpaceBetween,
  type SelectProps,
} from '@amzn/awsui-components-react';
import {
  getIvtClient,
  ListAssetsCommand,
  type AssetSummary,
} from '../../../client/IvtHubClient';
import { denialHint } from '../../../utils/authorization';
import type { RadarFpPayload } from '../useRadarDirectionAction';

/**
 * Reason popup for the "标记误报/不再展示" footer button of
 * RadarDirectionModal (DAT-9115, R19; induction §5.2; XIN-ANSWERS §7).
 *
 * UX: a small Cloudscape Modal on top of the direction detail modal, with
 *   - RadioGroup: 不是一个方向 (NOT_A_DIRECTION) / 已被现有资产覆盖 (COVERED_BY_ASSET)
 *   - Select of existing assets, scoped to the direction's industry, shown
 *     only when COVERED_BY_ASSET is picked.
 *   - Submit disabled until a reason is picked AND, for COVERED_BY_ASSET, an
 *     asset is chosen.
 *
 * Why NOT a Cloudscape Popover: a popover anchors to the footer button and
 * is dismissed by any outside click, which would race with the asset
 * dropdown — picking from the dropdown would dismiss the popover. A nested
 * Modal is clumsier but keeps the popover-outside-click problem away.
 *
 * The parent (RadarDirectionModal) owns the mutation hook and decides what
 * to do with the user's choice; this component only reports it through
 * `onConfirm` and renders the inline error the parent passes back in through
 * `submitting` / `error`. That keeps the "close on success + refresh the
 * dashboard" sequence in one place (the parent) and lets the popup stay
 * open on error without a round-trip through this component's state.
 */

export interface RadarFalsePositiveModalProps {
  /** `null` = closed. Opened by the direction modal when "标记误报" is clicked. */
  visible: boolean;
  /** Direction's industry — scopes the COVERED_BY_ASSET asset picker. */
  industry: string | undefined;
  submitting: boolean;
  error: string | null;
  onDismiss: () => void;
  onConfirm: (payload: RadarFpPayload) => void;
}

type Reason = 'NOT_A_DIRECTION' | 'COVERED_BY_ASSET';

/** Dropdown row — Select carries display name + id. */
interface AssetOption extends SelectProps.Option {
  value: string;
}

export function RadarFalsePositiveModal({
  visible,
  industry,
  submitting,
  error,
  onDismiss,
  onConfirm,
}: RadarFalsePositiveModalProps) {
  const [reason, setReason] = useState<Reason | null>(null);
  const [picked, setPicked] = useState<AssetOption | null>(null);
  // `assets` is cached PER industry — a direction's industry would never
  // change on the fly today, but the invariant used to be "the component
  // unmounts between directions" (parent conditionally renders), which
  // is purely by convention. Keying the cache by industry (not by
  // length) means a prop change serves the right list even if a future
  // refactor keeps the popup mounted across directions.
  const [assets, setAssets] = useState<{ industry: string | undefined; items: AssetSummary[] } | null>(null);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [assetsError, setAssetsError] = useState<string | null>(null);
  // Bumping this re-runs the asset load effect without flipping any of
  // the real deps — gives us an explicit "retry" affordance when the
  // first ListAssets call failed. Without this the user has to toggle
  // the radio back and forth to re-fire the load, which is non-obvious.
  const [retryToken, setRetryToken] = useState(0);

  // Load the industry's assets the first time the user picks COVERED_BY_ASSET.
  // Not on open: a user who always chooses NOT_A_DIRECTION never pays this
  // round-trip. A denial here is RECOVERABLE from the user's side (they can
  // pick NOT_A_DIRECTION) so render it as a FormField error with a 重试
  // button rather than blocking the whole popup. The parent gates the FP
  // button on `industry !== undefined`, but defend here too — a parent
  // change could drop the gate without this file knowing.
  //
  // Retry: the 重试 button clears `assets` + `assetsError` and bumps
  // `retryToken`, which combined with the "load only when assets is null"
  // guard below gives exactly one re-fire per click and avoids an auto-
  // retry loop on a persistent failure. (Keying the effect on `assetsError`
  // directly would spin: the catch sets error → re-render → effect sees
  // error non-null → fires again → fails → sets error → ...)
  useEffect(() => {
    if (reason !== 'COVERED_BY_ASSET') return;
    if (industry === undefined || industry === '') return;
    // Short-circuit once we have a successful cache for this industry.
    if (assets !== null && assets.industry === industry) return;
    // Short-circuit when we already have an error for this attempt — the
    // user retries explicitly via the retry button (bumps retryToken,
    // which resets `assets` + `assetsError`, so this guard clears).
    if (assetsError !== null) return;
    let cancelled = false;
    setLoadingAssets(true);
    // Drop stale selection when the industry changes under us.
    setPicked(null);
    (async () => {
      try {
        const out = await getIvtClient().send(new ListAssetsCommand({ industry }));
        if (!cancelled) setAssets({ industry, items: out.assets ?? [] });
      } catch (e) {
        if (!cancelled) setAssetsError(denialHint(e) ?? (e as Error).message ?? '加载资产失败');
      } finally {
        if (!cancelled) setLoadingAssets(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reason, industry, assets, assetsError, retryToken]);

  const retryAssets = () => {
    setAssets(null);
    setAssetsError(null);
    setRetryToken((n) => n + 1);
  };

  const assetOptions: AssetOption[] = useMemo(
    () =>
      (assets?.items ?? [])
        .filter((a): a is AssetSummary & { assetId: string } => typeof a.assetId === 'string')
        .map((a) => ({
          value: a.assetId,
          // Match NewAssetCreateModal's fallback: `name ?? useCase ?? assetId`,
          // so a legacy row without a display name is not shown as blank.
          label: a.name ?? a.useCase ?? a.assetId,
          description: a.industry,
        })),
    [assets],
  );

  const canSubmit =
    !submitting &&
    reason !== null &&
    (reason === 'NOT_A_DIRECTION' || (reason === 'COVERED_BY_ASSET' && picked !== null));

  const handleConfirm = () => {
    if (!canSubmit) return;
    if (reason === 'NOT_A_DIRECTION') {
      onConfirm({ reason: 'NOT_A_DIRECTION' });
      return;
    }
    // canSubmit guarantees picked !== null in this branch
    onConfirm({ reason: 'COVERED_BY_ASSET', dismissAssetId: picked!.value });
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="标记误报 / 不再展示"
      closeAriaLabel="关闭"
      size="small"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss} disabled={submitting}>
              取消
            </Button>
            <Button variant="primary" onClick={handleConfirm} disabled={!canSubmit} loading={submitting}>
              确认
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <FormField label="原因">
          <RadioGroup
            value={reason}
            onChange={({ detail }) => {
              setReason(detail.value as Reason);
              setPicked(null);
            }}
            items={[
              {
                value: 'NOT_A_DIRECTION',
                label: '不是一个方向',
                description: '这组证据不构成需要跟进的方向。',
              },
              {
                value: 'COVERED_BY_ASSET',
                label: '已被现有资产覆盖',
                description: '选择已经覆盖此方向的资产。',
              },
            ]}
          />
        </FormField>

        {reason === 'COVERED_BY_ASSET' && (
          <FormField
            label="覆盖资产"
            errorText={assetsError ?? undefined}
            description={industry !== undefined ? `当前行业：${industry}` : undefined}
            secondaryControl={
              // Only show the retry button after a load failure — the
              // async radio-toggle trick that used to re-fire the load
              // is non-obvious; an explicit affordance saves a round
              // through NOT_A_DIRECTION and back.
              assetsError !== null ? (
                <Button iconName="refresh" onClick={retryAssets} disabled={loadingAssets || submitting}>
                  重试
                </Button>
              ) : undefined
            }
          >
            <Select
              selectedOption={picked}
              onChange={({ detail }) => setPicked(detail.selectedOption as AssetOption)}
              options={assetOptions}
              placeholder="选择一个资产"
              statusType={loadingAssets ? 'loading' : 'finished'}
              loadingText="加载资产…"
              empty="该行业暂无资产"
              disabled={submitting || assetsError !== null}
            />
          </FormField>
        )}

        {error && (
          <Alert type="error" header="操作失败">
            {error}
          </Alert>
        )}
      </SpaceBetween>
    </Modal>
  );
}
